import React, { useEffect, useState, useRef } from "react";
import { convertLatexToMarkup } from "mathlive";
import "mathlive/static.css";
import "mathlive/fonts.css";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, Check,
  Loader, Eye, Printer, Save, X, Pencil, BookOpen,
  FileText, Layers, BarChart2,
} from "lucide-react";
import {
  lmsGetManualPaperById, lmsUpdateManualPaper,
  lmsCreateSection, lmsUpdateSection, lmsDeleteSection,
  lmsCreateQuestion, lmsUpdateQuestion, lmsDeleteQuestion,
  lmsUpdateOption, lmsGetExams, lmsGetChapters, lmsUpdateExam,
} from "../api/lmsApi";
import "../lms.css";

const Q_TYPES = [
  { value: "mcq",             label: "Multiple Choice (MCQ)" },
  { value: "true_false",      label: "True / False" },
  { value: "fill_blank",      label: "Fill in the Blank" },
  { value: "short_answer",    label: "Short Answer" },
  { value: "long_answer",     label: "Long Answer" },
  { value: "match_following", label: "Match the Following" },
];
const Q_TYPE_LABELS = Object.fromEntries(Q_TYPES.map((t) => [t.value, t.label]));
const SECTION_LABELS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const inp = (extra = {}) => ({
  width: "100%", padding: "8px 10px", borderRadius: 7, fontSize: 13,
  border: "1.5px solid #e5e7eb", outline: "none", color: "#111827",
  background: "#fff", boxSizing: "border-box", ...extra,
});

/* ── ConfirmModal ── */
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, width: 380, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#dc2626,#ef4444)" }} />
        <div style={{ padding: "28px 24px 20px", textAlign: "center" }}>
          <div style={{
            width: 52, height: 52, borderRadius: "50%", background: "#fef2f2",
            border: "2px solid #fecaca", display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 14px",
          }}>
            <Trash2 size={22} color="#dc2626" />
          </div>
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.7 }}>{message}</div>
        </div>
        <div style={{ display: "flex", gap: 10, padding: "0 24px 20px" }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: "9px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: "9px 0", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "#dc2626", color: "#fff", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}><Trash2 size={13} /> Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Match the Following helpers ── */
const EMPTY_COL_A   = () => ["", "", ""];
const EMPTY_COL_B   = () => ["", "", ""];
const EMPTY_ANSWERS = () => ["", "", ""];

function parseMatchData(text) {
  if (!text) return { instruction: "", colA: EMPTY_COL_A(), colB: EMPTY_COL_B(), answers: EMPTY_ANSWERS() };
  try {
    const p = JSON.parse(text);
    // New format: {instruction, colA, colB, answers}
    if (Array.isArray(p.colA)) {
      return {
        instruction: p.instruction || "",
        colA: p.colA.length ? p.colA : EMPTY_COL_A(),
        colB: p.colB?.length ? p.colB : EMPTY_COL_B(),
        answers: p.answers?.length ? p.answers : EMPTY_ANSWERS(),
      };
    }
    // Legacy format: {instruction, pairs:[{a,b}]}
    if (Array.isArray(p.pairs) && p.pairs.length) {
      const colA    = p.pairs.map(pr => pr.a || "");
      const colB    = p.pairs.map(pr => pr.b || "");
      const answers = colA.map((_, i) => String.fromCharCode(97 + i)); // a, b, c (direct match)
      return { instruction: p.instruction || "", colA, colB, answers };
    }
  } catch {}
  return { instruction: "", colA: EMPTY_COL_A(), colB: EMPTY_COL_B(), answers: EMPTY_ANSWERS() };
}

// Keep for legacy callers
function parseMatchPairs(text) {
  const { colA, colB, answers } = parseMatchData(text);
  return colA.map((a, i) => {
    const letterIdx = answers[i] ? answers[i].charCodeAt(0) - 97 : i;
    return { a, b: colB[letterIdx] || colB[i] || "" };
  });
}

function serializeMatchData({ instruction = "", colA = [], colB = [], answers = [] }) {
  return JSON.stringify({ instruction, colA, colB, answers });
}

/* ── MathPreview ── */
function MathPreview({ latex }) {
  if (!latex) return null;
  const hasLatex = /[\\^_{}]/.test(latex);
  if (!hasLatex) return <span style={{ fontFamily: "inherit" }}>{latex}</span>;
  try {
    const html = convertLatexToMarkup(latex, { mathstyle: "textstyle" });
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  } catch {
    return <span>{latex}</span>;
  }
}

/* ── Match the Following Editor ── */
function MatchEditor({ value, onChange, onSave }) {
  const init = parseMatchData(value);
  const [instruction, setInstruction] = useState(init.instruction);
  const [colA,        setColA]        = useState(init.colA);
  const [colB,        setColB]        = useState(init.colB);
  const [answers,     setAnswers]     = useState(init.answers);

  const notify = (a, b, ans, ins) => onChange(serializeMatchData({ instruction: ins, colA: a, colB: b, answers: ans }));

  const updateIns  = (v) => { setInstruction(v); notify(colA, colB, answers, v); };
  const updateColA = (i, v) => { const n = [...colA]; n[i] = v; setColA(n); notify(n, colB, answers, instruction); };
  const updateColB = (i, v) => { const n = [...colB]; n[i] = v; setColB(n); notify(colA, n, answers, instruction); };
  const updateAns  = (i, v) => { const n = [...answers]; n[i] = v; setAnswers(n); notify(colA, colB, n, instruction); };

  const addItem = () => {
    const na = [...colA, ""]; const nb = [...colB, ""]; const nans = [...answers, ""];
    setColA(na); setColB(nb); setAnswers(nans); notify(na, nb, nans, instruction);
  };
  const removeItem = (i) => {
    if (colA.length === 1) return;
    const na = colA.filter((_, idx) => idx !== i);
    const nb = colB.filter((_, idx) => idx !== i);
    const nans = answers.filter((_, idx) => idx !== i);
    setColA(na); setColB(nb); setAnswers(nans); notify(na, nb, nans, instruction);
  };

  const letters = colB.map((_, i) => String.fromCharCode(97 + i));

  return (
    <div style={{ marginTop: 10 }}>
      {/* Instruction */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: 1, display: "block", marginBottom: 4 }}>QUESTION INSTRUCTION</label>
        <input
          value={instruction}
          onChange={e => updateIns(e.target.value)}
          onBlur={onSave}
          placeholder="e.g. Match the words with their opposites:"
          style={{ ...inp(), width: "100%", boxSizing: "border-box" }}
        />
      </div>

      {/* Column A + Column B side by side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        {/* Column A */}
        <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ background: "#f0f9ff", padding: "8px 12px", borderBottom: "1px solid #e5e7eb", fontSize: 11, fontWeight: 700, color: "#0369a1" }}>
            COLUMN A — Items to Match
          </div>
          {colA.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderBottom: i < colA.length - 1 ? "1px solid #f3f4f6" : "none" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5", minWidth: 20 }}>{i + 1}.</span>
              <input value={item} onChange={e => updateColA(i, e.target.value)} onBlur={onSave} placeholder={`Item ${i + 1}`} style={{ ...inp(), flex: 1 }} />
              <button onClick={() => removeItem(i)} disabled={colA.length === 1} style={{
                width: 22, height: 22, borderRadius: 5, border: "1px solid #fecaca", flexShrink: 0,
                background: colA.length === 1 ? "#f9fafb" : "#fff5f5",
                color: colA.length === 1 ? "#d1d5db" : "#dc2626",
                cursor: colA.length === 1 ? "default" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}><X size={10} /></button>
            </div>
          ))}
        </div>

        {/* Column B */}
        <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ background: "#f0fdf4", padding: "8px 12px", borderBottom: "1px solid #e5e7eb", fontSize: 11, fontWeight: 700, color: "#166534" }}>
            COLUMN B — Answer Options (shown to students)
          </div>
          {colB.map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderBottom: i < colB.length - 1 ? "1px solid #f3f4f6" : "none" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#16a34a", minWidth: 20 }}>{letters[i]}.</span>
              <input value={item} onChange={e => updateColB(i, e.target.value)} onBlur={onSave} placeholder={`Option ${letters[i]}`} style={{ ...inp(), flex: 1 }} />
            </div>
          ))}
        </div>
      </div>

      <button onClick={addItem} style={{
        marginBottom: 16, padding: "5px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600,
        border: "1.5px dashed #a5b4fc", background: "#f5f3ff", color: "#4f46e5",
        cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
      }}>
        <Plus size={12} /> Add Row
      </button>

      {/* Answer key — which Column B matches each Column A */}
      <div style={{ border: "1.5px solid #fbbf24", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ background: "#fffbeb", padding: "8px 14px", borderBottom: "1px solid #fde68a", fontSize: 11, fontWeight: 700, color: "#92400e" }}>
          ANSWER KEY — Select the correct Column B match for each Column A item
        </div>
        {colA.map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", borderBottom: i < colA.length - 1 ? "1px solid #fef9c3" : "none", background: "#fff" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", minWidth: 80 }}>
              <span style={{ color: "#4f46e5", fontWeight: 700, marginRight: 4 }}>{i + 1}.</span>{item || `Item ${i + 1}`}
            </span>
            <span style={{ color: "#9ca3af" }}>→</span>
            <select
              value={answers[i] || ""}
              onChange={e => { updateAns(i, e.target.value); if (onSave) setTimeout(onSave, 50); }}
              style={{ padding: "6px 10px", borderRadius: 7, border: `1.5px solid ${answers[i] ? "#fbbf24" : "#e5e7eb"}`, background: answers[i] ? "#fffbeb" : "#fff", fontSize: 13, cursor: "pointer", outline: "none" }}
            >
              <option value="">-- Select Answer --</option>
              {colB.map((b, j) => (
                <option key={j} value={letters[j]}>{letters[j]}. {b || `Option ${letters[j]}`}</option>
              ))}
            </select>
            {answers[i] && (
              <span style={{ fontSize: 12, color: "#16a34a", fontWeight: 700 }}>
                ✓ Answer: {answers[i]}. {colB[answers[i].charCodeAt(0) - 97] || ""}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── MCQ Options — radio-style vertical list ── */
function McqOptions({ options, onOptionSaved }) {
  const [localValues, setLocalValues] = useState(() => {
    const m = {};
    options.forEach((o) => { m[o.id] = o.option_text || ""; });
    return m;
  });
  const [saving, setSaving] = useState({});

  useEffect(() => {
    setLocalValues((prev) => {
      const m = { ...prev };
      options.forEach((o) => { if (m[o.id] === undefined) m[o.id] = o.option_text || ""; });
      return m;
    });
  }, [options]);

  const saveOption = async (opt) => {
    const text = localValues[opt.id] ?? "";
    if (text === (opt.option_text || "")) return;
    setSaving((p) => ({ ...p, [opt.id]: true }));
    try { await lmsUpdateOption(opt.id, { option_text: text }); onOptionSaved(); }
    catch { toast.error("Failed to save option"); }
    finally { setSaving((p) => ({ ...p, [opt.id]: false })); }
  };

  const markCorrect = async (opt) => {
    if (opt.is_correct) return;
    setSaving((p) => ({ ...p, [opt.id]: true }));
    try { await lmsUpdateOption(opt.id, { is_correct: 1 }); onOptionSaved(); }
    catch { toast.error("Failed to update"); }
    finally { setSaving((p) => ({ ...p, [opt.id]: false })); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
      {options.map((opt) => (
        <div key={opt.id} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Radio circle — click to mark correct */}
          <button
            onClick={() => markCorrect(opt)}
            title={opt.is_correct ? "Correct answer" : "Click to mark as correct"}
            style={{
              width: 28, height: 28, borderRadius: "50%", flexShrink: 0, cursor: "pointer",
              border: opt.is_correct ? "none" : "2px solid #d1d5db",
              background: opt.is_correct ? "#16a34a" : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.15s",
            }}
          >
            {saving[opt.id]
              ? <Loader size={12} className="ms2-spin" color={opt.is_correct ? "#fff" : "#9ca3af"} />
              : opt.is_correct
                ? <Check size={14} color="#fff" strokeWidth={3} />
                : null
            }
          </button>

          {/* Option text input */}
          <input
            type="text"
            value={localValues[opt.id] ?? ""}
            placeholder={`Option ${opt.option_label}`}
            onChange={(e) => setLocalValues((p) => ({ ...p, [opt.id]: e.target.value }))}
            onBlur={() => saveOption(opt)}
            style={{
              flex: 1, padding: "9px 14px", borderRadius: 8,
              border: "1.5px solid #e5e7eb", fontSize: 14, color: "#111827",
              outline: "none", background: "#fff", fontFamily: "inherit",
              transition: "border-color 0.15s",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#a5b4fc"; }}
            onBlurCapture={(e) => { e.target.style.borderColor = "#e5e7eb"; }}
          />

          {/* Remove button (visual only — API may not support deleting options) */}
          <button
            disabled
            style={{
              width: 28, height: 28, borderRadius: 6, flexShrink: 0,
              border: "none", background: "transparent",
              color: "#d1d5db", cursor: "default",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

/* ── Section type colors ── */
const SEC_COLORS = {
  mcq: "#4f46e5", true_false: "#0891b2", fill_blank: "#d97706",
  short_answer: "#16a34a", long_answer: "#7c3aed", match_following: "#dc2626",
};

/* ── Question Card — redesigned ── */
function QuestionCard({ question, index, sectionType, onDelete, onRefresh, locked, chapters = [] }) {
  const [text,   setText]   = useState(question.question_text);
  const [marks,  setMarks]  = useState(question.marks ?? "1");
  const [answer, setAnswer] = useState(question.correct_answer || "");
  const [saving, setSaving] = useState(false);
  const [qFocused, setQFocused] = useState(false);

  const hasMcqOptions = sectionType === "mcq" || sectionType === "true_false";
  const color = SEC_COLORS[sectionType] || "#4f46e5";

  const handleSaveText = async () => {
    if (text.trim() === question.question_text) return;
    setSaving(true);
    try {
      await lmsUpdateQuestion(question.id, { question_text: text.trim() });
      onRefresh();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const handleSaveMarks = async () => {
    if (String(marks) === String(question.marks ?? "")) return;
    try { await lmsUpdateQuestion(question.id, { marks: marks || null }); onRefresh(); }
    catch {}
  };

  const handleSaveAnswer = async () => {
    if (answer === (question.correct_answer || "")) return;
    try { await lmsUpdateQuestion(question.id, { correct_answer: answer || null }); onRefresh(); }
    catch {}
  };

  return (
    <div style={{
      background: "#fff", borderRadius: 12, marginBottom: 12,
      border: `1.5px solid ${color}20`,
      boxShadow: `0 2px 8px ${color}0a`,
      overflow: "hidden",
    }}>
      {/* Question header bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 16px",
        background: `linear-gradient(135deg, ${color}08, #fafbff)`,
        borderBottom: `1px solid ${color}15`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            color: "#fff", fontSize: 12, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 2px 6px ${color}40`,
          }}>
            Q{index}
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#6b7280" }}>
            {Q_TYPE_LABELS[sectionType] || sectionType}
          </span>
          {/* Marks inline badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: 4 }}>
            <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>Marks:</span>
            <input
              type="number"
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
              onBlur={handleSaveMarks}
              min="0.5" step="0.5"
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: "0 0 72px", width: "72px", padding: "3px 8px", borderRadius: 6,
                fontSize: 13, fontWeight: 800, border: `1.5px solid ${color}40`,
                outline: "none", color, textAlign: "center", background: `${color}08`,
              }}
              onFocus={(e) => { e.target.style.borderColor = color; }}
              onBlurCapture={(e) => { e.target.style.borderColor = `${color}40`; }}
            />
          </div>
          {/* Chapter assignment */}
          {chapters.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: 6 }}>
              <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>Chapter:</span>
              <select
                value={question.chapter_id || ""}
                onChange={async (e) => {
                  const val = e.target.value ? parseInt(e.target.value) : null;
                  try {
                    await lmsUpdateQuestion(question.id, { chapter_id: val });
                    onRefresh();
                  } catch {
                    toast.error("Failed to update chapter");
                  }
                }}
                style={{
                  padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                  border: "1.5px solid #cbd5e1", background: question.chapter_id ? "#f0fdf4" : "#fff",
                  color: question.chapter_id ? "#15803d" : "#64748b", cursor: "pointer", outline: "none",
                  maxWidth: 160,
                }}
              >
                <option value="">(No Chapter)</option>
                {chapters.map((ch) => (
                  <option key={ch.id} value={ch.id}>
                    {ch.chapter_number ? `Ch ${ch.chapter_number}: ` : ""}{ch.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <button onClick={onDelete} style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 12, color: "#9ca3af", background: "transparent",
          border: "1px solid #e5e7eb", cursor: "pointer", padding: "4px 10px", borderRadius: 6,
        }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#dc2626"; e.currentTarget.style.borderColor = "#fecaca"; e.currentTarget.style.background = "#fff5f5"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "transparent"; }}
        >
          <X size={12} /> Remove
        </button>
      </div>
      <div style={{ padding: "14px 16px" }}>

      {/* Question textarea */}
      {sectionType === "match_following" ? (
        <MatchEditor value={text} onChange={setText} onSave={handleSaveText} />
      ) : (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleSaveText}
          onFocus={() => setQFocused(true)}
          placeholder={`Type question ${index} here...`}
          rows={2}
          style={{
            width: "100%", boxSizing: "border-box", display: "block",
            border: `2px solid ${qFocused ? color : "#e5e7eb"}`,
            borderRadius: 10, padding: "12px 14px", fontSize: 14,
            fontFamily: "inherit", outline: "none", resize: "vertical",
            color: "#111827", lineHeight: 1.6, background: "#fff",
            transition: "border-color 0.15s",
          }}
          onBlurCapture={() => setQFocused(false)}
        />
      )}

      {saving && (
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
          <Loader size={10} className="ms2-spin" /> saving...
        </div>
      )}

      {/* MCQ / True-False options */}
      {hasMcqOptions && question.options?.length > 0 && (
        <McqOptions options={question.options} onOptionSaved={onRefresh} />
      )}

      {/* Fill-blank / Short answer — answer field */}
      {(sectionType === "fill_blank" || sectionType === "short_answer") && (
        <div style={{ marginTop: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 5 }}>Answer</label>
          <input
            type="text"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onBlur={handleSaveAnswer}
            placeholder="Correct answer..."
            style={inp()}
          />
        </div>
      )}

      </div>
    </div>
  );
}

const ALL_OPT_LABELS = ["A", "B", "C", "D", "E", "F"];
const TF_LABELS      = ["True", "False"];

/* ── Section Card ── */
function SectionCard({ section, onRefresh, onDelete, maxQuestions, currentTotalQ, locked, chapters = [], examTotalMarks = 0, currentTotalMarks = 0 }) {
  const isMcq = section.question_type === "mcq";
  const isTF  = section.question_type === "true_false";

  const defaultOpts = () =>
    isMcq ? [{ label: "A", text: "" }, { label: "B", text: "" }]
    : isTF ? TF_LABELS.map((l) => ({ label: l, text: l }))
    : [];

  const [open,        setOpen]       = useState(true);
  const [addingQ,     setAddingQ]    = useState(false);
  const [newQText,    setNewQText]   = useState(
    section.question_type === "match_following" ? serializeMatchData({ instruction: "", colA: EMPTY_COL_A(), colB: EMPTY_COL_B(), answers: EMPTY_ANSWERS() }) : ""
  );
  const [newOpts,     setNewOpts]    = useState(defaultOpts);
  const [correctIdx,  setCorrectIdx] = useState(null);   // index of correct option
  const [newMarks,    setNewMarks]   = useState("");
  const [newChapterId, setNewChapterId] = useState("");
  const [newQFocused, setNewQFocused] = useState(false);
  const [qTextError,  setQTextError]  = useState("");
  const [savingQ,     setSavingQ]    = useState(false);
  const [confirmQ,    setConfirmQ]   = useState(null);

  const resetForm = () => {
    setNewQText(section.question_type === "match_following" ? serializeMatchData({ instruction: "", colA: EMPTY_COL_A(), colB: EMPTY_COL_B(), answers: EMPTY_ANSWERS() }) : "");
    setNewOpts(defaultOpts());
    setCorrectIdx(null);
    setNewMarks("");
    setNewChapterId("");
    setQTextError("");
    setAddingQ(false);
  };

  const addOption = () => {
    if (newOpts.length >= ALL_OPT_LABELS.length) return;
    setNewOpts((p) => [...p, { label: ALL_OPT_LABELS[p.length], text: "" }]);
  };

  const removeOption = (i) => {
    if (newOpts.length <= 2) return;
    setNewOpts((p) => p.filter((_, idx) => idx !== i).map((o, idx) => ({ ...o, label: ALL_OPT_LABELS[idx] })));
    if (correctIdx === i) setCorrectIdx(null);
    else if (correctIdx > i) setCorrectIdx((c) => c - 1);
  };

  const handleAddQuestion = async () => {
    const isMatch = section.question_type === "match_following";
    const hasContent = isMatch
      ? (() => { try { const p = JSON.parse(newQText); return Array.isArray(p.colA) ? p.colA.some((a) => a.trim()) : p.pairs?.some((pr) => pr.a.trim()); } catch { return false; } })()
      : newQText.trim().length > 0;
    if (!hasContent) {
      setQTextError(isMatch ? "Add at least one item in Column A" : "Question text is required");
      return;
    }
    setQTextError("");
    setSavingQ(true);
    try {
      const res = await lmsCreateQuestion(section.id, {
        question_text: newQText.trim(),
        marks: newMarks || null,
        chapter_id: newChapterId || null,
      });
      // Update option texts and mark correct
      const createdQ = res?.data?.data || res?.data;
      if (createdQ?.options?.length && (isMcq || isTF)) {
        await Promise.all(
          createdQ.options.map((opt, i) => {
            const txt   = isTF ? newOpts[i]?.label : newOpts[i]?.text?.trim();
            const isCorr = correctIdx === i ? 1 : 0;
            const payload = {};
            if (txt)    payload.option_text = txt;
            if (isCorr) payload.is_correct  = 1;
            if (Object.keys(payload).length)
              return lmsUpdateOption(opt.id, payload).catch(() => {});
            return Promise.resolve();
          })
        );
      }
      resetForm();
      onRefresh();
      // Warn if marks will exceed after this question
      const qMarks = parseFloat(newMarks || section.marks_per_q || 1);
      const newTotal = currentTotalMarks + qMarks;
      if (examTotalMarks > 0 && newTotal > examTotalMarks) {
        toast.error(`Marks exceeded! Paper now has ${newTotal} marks but exam allows only ${examTotalMarks}. Adjust Marks/Q.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add question");
    } finally { setSavingQ(false); }
  };

  const handleDeleteQuestion = async () => {
    try {
      await lmsDeleteQuestion(confirmQ.id);
      toast.success("Question deleted");
      setConfirmQ(null);
      onRefresh();
    } catch { toast.error("Failed to delete"); }
  };

  const color      = SEC_COLORS[section.question_type] || "#4f46e5";
  const qCount     = section.questions?.length || 0;
  const secMarks   = section.questions?.reduce((s, q) => s + parseFloat(q.marks || section.marks_per_q || 0), 0) || 0;
  const qTypeLabel = Q_TYPE_LABELS[section.question_type] || section.question_type;

  return (
    <div style={{
      borderRadius: 16, marginBottom: 16, overflow: "hidden",
      border: `1.5px solid ${color}30`,
      boxShadow: `0 2px 12px ${color}10`,
      background: "#fff",
    }}>
      {/* Section header */}
      <div style={{
        padding: "0",
        borderBottom: open ? `1.5px solid ${color}20` : "none",
      }}>
        {/* Color accent top bar */}
        <div style={{ height: 3, background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
        <div style={{
          padding: "14px 20px",
          display: "flex", alignItems: "center", gap: 14,
          background: `linear-gradient(135deg, ${color}08, #ffffff)`,
        }}>
          {/* Section badge */}
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: `linear-gradient(135deg, ${color}, ${color}cc)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 3px 10px ${color}40`,
          }}>
            <span style={{ fontWeight: 900, fontSize: 16, color: "#fff" }}>{section.section_label}</span>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: "#111827" }}>
                Section {section.section_label}
              </span>
              {section.title && <span style={{ fontSize: 13, color: "#6b7280" }}>· {section.title}</span>}
              <span style={{
                fontSize: 11, fontWeight: 700, color: "#fff",
                background: color, borderRadius: 20, padding: "2px 10px",
              }}>{qTypeLabel}</span>
            </div>
            <div style={{ display: "flex", gap: 16, marginTop: 5, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, display: "inline-block" }} />
                {qCount} question{qCount !== 1 ? "s" : ""}
              </span>
              <span style={{ fontSize: 12, color: "#6b7280" }}>{section.marks_per_q} mark each</span>
              <span style={{ fontSize: 12, fontWeight: 700, color }}>Total: {secMarks.toFixed(1)} marks</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
            <button onClick={() => setOpen((p) => !p)} style={{
              display: "flex", alignItems: "center", gap: 5, padding: "7px 12px",
              borderRadius: 8, border: "1.5px solid #e5e7eb",
              background: "#fff", color: "#374151", cursor: "pointer", fontSize: 12, fontWeight: 600,
            }}>
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              {open ? "Collapse" : "Expand"}
            </button>
            <button onClick={onDelete} title="Delete section" style={{
              width: 34, height: 34, borderRadius: 8, border: "1.5px solid #fecaca",
              background: "#fff5f5", color: "#dc2626", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div style={{ padding: "20px 20px 16px", background: "#fafbff" }}>
          {qCount === 0 && !addingQ && (
            <div style={{
              textAlign: "center", padding: "32px 20px",
              background: "#fff", borderRadius: 12,
              border: `2px dashed ${color}40`,
              marginBottom: 16,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12, background: `${color}12`,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 12px",
              }}>
                <Plus size={22} color={color} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 4 }}>No questions yet</div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>Click <strong style={{ color }}>+ Add Question</strong> below to start adding questions.</div>
            </div>
          )}

          {section.questions?.map((q, qi) => (
            <QuestionCard
              key={q.id}
              question={q}
              index={qi + 1}
              sectionType={section.question_type}
              chapters={chapters}
              onRefresh={onRefresh}
              onDelete={() => setConfirmQ(q)}
            />
          ))}

          {/* Limit reached banners */}
          {!addingQ && maxQuestions > 0 && currentTotalQ >= maxQuestions && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
              background: "#fef9c3", border: "1.5px solid #fde68a", borderRadius: 10,
              marginTop: qCount > 0 ? 6 : 0,
            }}>
              <span style={{ fontSize: 16 }}>🚫</span>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e" }}>
                Question limit reached ({currentTotalQ}/{maxQuestions}) — remove a question to add a new one.
              </div>
            </div>
          )}
          {!addingQ && examTotalMarks > 0 && currentTotalMarks >= examTotalMarks && (
            <div style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
              background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 10,
              marginTop: 6,
            }}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#dc2626" }}>
                Marks limit reached ({currentTotalMarks}/{examTotalMarks}) — adjust section Marks/Q before adding more.
              </div>
            </div>
          )}

          {addingQ ? (
            <div style={{
              background: "#fff", borderRadius: 14, border: `2px solid ${color}40`,
              overflow: "hidden", marginTop: 10,
              boxShadow: `0 4px 16px ${color}12`,
            }}>
              {/* Form header */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 16px",
                background: `linear-gradient(135deg, ${color}10, #f8f9ff)`,
                borderBottom: `1.5px solid ${color}20`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                    color: "#fff", fontSize: 13, fontWeight: 800,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    Q{qCount + 1}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>New Question</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color, background: `${color}15`, borderRadius: 20, padding: "2px 8px" }}>
                    {Q_TYPE_LABELS[section.question_type]}
                  </span>
                </div>
              </div>
              <div style={{ padding: "16px 18px" }}>

              {/* Question input */}
              {section.question_type === "match_following" ? (
                <MatchEditor value={newQText} onChange={setNewQText} />
              ) : (
                <textarea
                  value={newQText}
                  onChange={(e) => { setNewQText(e.target.value); setQTextError(""); }}
                  onFocus={() => setNewQFocused(true)}
                  onBlur={() => setNewQFocused(false)}
                  placeholder={`Type question ${qCount + 1} here...`}
                  rows={2}
                  style={{
                    width: "100%", boxSizing: "border-box", display: "block",
                    border: `2px solid ${qTextError ? "#dc2626" : newQFocused ? color : "#e5e7eb"}`,
                    borderRadius: 10, padding: "12px 14px", fontSize: 14,
                    fontFamily: "inherit", outline: "none", resize: "vertical",
                    color: "#111827", lineHeight: 1.6,
                    background: qTextError ? "#fff5f5" : "#fff",
                    transition: "border-color 0.15s",
                  }}
                />
              )}
              {qTextError && (
                <div style={{ fontSize: 11, fontWeight: 700, color: "#dc2626", marginTop: 4 }}>
                  ⚠ {qTextError}
                </div>
              )}

              {/* MCQ / True-False option inputs */}
              {(isMcq || isTF) && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
                  {newOpts.map((opt, i) => {
                    const isCorrect = correctIdx === i;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {/* Clickable radio */}
                        <button
                          type="button"
                          onClick={() => setCorrectIdx(isCorrect ? null : i)}
                          title="Mark as correct answer"
                          style={{
                            width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                            border: isCorrect ? "none" : "2px solid #d1d5db",
                            background: isCorrect ? "#16a34a" : "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer", transition: "all 0.15s",
                          }}
                        >
                          {isCorrect && <Check size={14} color="#fff" strokeWidth={3} />}
                        </button>

                        {/* Option text input */}
                        <input
                          type="text"
                          value={isTF ? opt.label : opt.text}
                          readOnly={isTF}
                          onChange={(e) => {
                            const next = [...newOpts];
                            next[i] = { ...next[i], text: e.target.value };
                            setNewOpts(next);
                          }}
                          placeholder={`Option ${opt.label}`}
                          style={{
                            flex: 1, padding: "9px 14px", borderRadius: 8,
                            border: isCorrect ? "1.5px solid #86efac" : "1.5px solid #e5e7eb",
                            fontSize: 14, color: "#111827",
                            outline: "none",
                            background: isCorrect ? "#f0fdf4" : isTF ? "#f9fafb" : "#fff",
                            fontFamily: "inherit", transition: "all 0.15s",
                          }}
                          onFocus={(e) => { if (!isCorrect) e.target.style.borderColor = "#a5b4fc"; }}
                          onBlur={(e) => { if (!isCorrect) e.target.style.borderColor = "#e5e7eb"; }}
                        />

                        {/* Remove option */}
                        {isMcq && newOpts.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeOption(i)}
                            style={{
                              width: 26, height: 26, borderRadius: 6, flexShrink: 0,
                              border: "1px solid #fecaca", background: "#fff5f5",
                              color: "#dc2626", cursor: "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    );
                  })}

                </div>
              )}

              {/* Bottom action row — single line */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8, marginTop: 14,
                flexWrap: "nowrap", overflowX: "auto",
              }}>
                {/* Add Option (MCQ only) */}
                {isMcq && newOpts.length < ALL_OPT_LABELS.length && (
                  <button type="button" onClick={addOption} style={{
                    flexShrink: 0, fontSize: 12, fontWeight: 700, color: "#4f46e5",
                    background: "#ede9fe", border: "1.5px dashed #a5b4fc",
                    borderRadius: 7, cursor: "pointer",
                    padding: "7px 14px", display: "flex", alignItems: "center", gap: 5,
                    whiteSpace: "nowrap",
                  }}>
                    <Plus size={12} /> Add Option
                  </button>
                )}

                {/* push right */}
                <div style={{ flex: 1 }} />

                {/* Chapter */}
                {chapters.length > 0 && (
                  <>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", flexShrink: 0 }}>Chapter</span>
                    <select
                      value={newChapterId}
                      onChange={(e) => setNewChapterId(e.target.value)}
                      style={{
                        flexShrink: 0, padding: "7px 10px", borderRadius: 7, fontSize: 12, fontWeight: 600,
                        border: "1.5px solid #e5e7eb", outline: "none", color: "#111827",
                        background: "#fff", maxWidth: 160,
                      }}
                    >
                      <option value="">(Optional Chapter)</option>
                      {chapters.map((ch) => (
                        <option key={ch.id} value={ch.id}>
                          {ch.chapter_number ? `Ch ${ch.chapter_number}: ` : ""}{ch.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {/* Marks */}
                <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", flexShrink: 0 }}>Marks</span>
                <input
                  type="number"
                  value={newMarks}
                  onChange={(e) => setNewMarks(e.target.value)}
                  placeholder={section.marks_per_q || "1"}
                  min="0.5" step="0.5"
                  style={{
                    flexShrink: 0, flexGrow: 0, width: 72, minWidth: 72, maxWidth: 72, padding: "7px 10px", borderRadius: 7, fontSize: 13, fontWeight: 700,
                    border: "1.5px solid #e5e7eb", outline: "none", color: "#111827", textAlign: "center",
                    MozAppearance: "textfield", appearance: "textfield",
                  }}
                />

                {/* Save */}
                <button onClick={handleAddQuestion} disabled={savingQ} style={{
                  flexShrink: 0, padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                  border: "none", background: `linear-gradient(135deg,${color},${color}cc)`,
                  color: "#fff", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 5,
                  boxShadow: `0 2px 8px ${color}44`, whiteSpace: "nowrap",
                }}>
                  {savingQ ? <Loader size={12} className="ms2-spin" /> : <Check size={13} />}
                  Save
                </button>

                {/* Cancel */}
                <button onClick={resetForm} style={{
                  flexShrink: 0, padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: "1.5px solid #e5e7eb", background: "#fff",
                  color: "#6b7280", cursor: "pointer", whiteSpace: "nowrap",
                }}>
                  Cancel
                </button>
              </div>
              </div>
            </div>
          ) : (
            !(
              (maxQuestions > 0 && currentTotalQ >= maxQuestions) ||
              (examTotalMarks > 0 && currentTotalMarks >= examTotalMarks)
            ) && (
              <button onClick={() => setAddingQ(true)} style={{
                display: "flex", alignItems: "center", gap: 6,
                fontSize: 13, fontWeight: 700, color,
                background: `${color}10`, border: `1.5px dashed ${color}60`,
                borderRadius: 9, cursor: "pointer",
                padding: "9px 18px", marginTop: qCount > 0 ? 10 : 0,
                width: "100%", justifyContent: "center",
                transition: "background 0.15s",
              }}>
                <Plus size={15} /> Add Question
              </button>
            )
          )}
        </div>
      )}

      {confirmQ && (
        <ConfirmModal
          message={<>Delete this question? This cannot be undone.</>}
          onConfirm={handleDeleteQuestion}
          onCancel={() => setConfirmQ(null)}
        />
      )}
    </div>
  );
}

/* ── Add Section Modal ── */
function AddSectionModal({ nextLabel, onClose, onSave, defaultMarksPerQ = "1", pendingMarks = null, pendingQ = null }) {
  const [form, setForm] = useState({
    section_label: nextLabel, title: "",
    question_type: "mcq", marks_per_q: defaultMarksPerQ, instructions: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, width: 460, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#7c3aed)" }} />
        <div style={{
          padding: "18px 22px 14px", borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Add Section</div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#9ca3af" }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Pending info banner */}
          {(pendingMarks !== null || pendingQ !== null) && (
            <div style={{
              background: "linear-gradient(135deg,#eff6ff,#f0fdf4)",
              border: "1.5px solid #bfdbfe", borderRadius: 10,
              padding: "10px 14px", display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ fontSize: 18 }}>📋</span>
              <div style={{ fontSize: 12, color: "#1e40af", fontWeight: 600, lineHeight: 1.6 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: "#1e3a8a", marginBottom: 2 }}>
                  Paper Status
                </div>
                {pendingMarks !== null && pendingMarks > 0 && (
                  <div>⚡ <strong>{pendingMarks} mark{pendingMarks !== 1 ? "s" : ""}</strong> still needed to reach exam total</div>
                )}
                {pendingMarks !== null && pendingMarks <= 0 && (
                  <div style={{ color: "#dc2626" }}>⚠ Marks already at or over limit — adjust existing section Marks/Q</div>
                )}
                {pendingQ !== null && pendingQ > 0 && (
                  <div>❓ <strong>{pendingQ} question{pendingQ !== 1 ? "s" : ""}</strong> still needed to reach exam total</div>
                )}
                {pendingQ !== null && pendingQ <= 0 && (
                  <div style={{ color: "#dc2626" }}>⚠ Question limit already reached</div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Label</label>
              <select style={inp({ marginTop: 4 })} value={form.section_label}
                onChange={(e) => set("section_label", e.target.value)}>
                {SECTION_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Section Title</label>
              <input style={inp({ marginTop: 4 })} value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="Multiple Choice Questions" />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Question Type</label>
              <select style={inp({ marginTop: 4 })} value={form.question_type}
                onChange={(e) => set("question_type", e.target.value)}>
                {Q_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Marks/Q</label>
              <input type="number" style={inp({ marginTop: 4 })} value={form.marks_per_q}
                onChange={(e) => set("marks_per_q", e.target.value)} min="0.5" step="0.5" />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Section Instructions</label>
            <textarea style={inp({ marginTop: 4, resize: "vertical" })} rows={2}
              value={form.instructions} onChange={(e) => set("instructions", e.target.value)}
              placeholder="e.g. Attempt all questions" />
          </div>
        </div>

        <div style={{ padding: "12px 22px 18px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          }}>
            {saving ? <Loader size={12} className="ms2-spin" /> : <Plus size={13} />}
            Add Section
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Print Preview Modal ── */
function PrintPreview({ paper, onClose }) {
  const ref = useRef();

  const handlePrint = () => {
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>${paper.title}</title>
      <link id="ml-css" rel="stylesheet" href="https://unpkg.com/mathlive/mathlive-static.css" />
      <link rel="stylesheet" href="https://unpkg.com/mathlive/mathlive-fonts.css" />
      <style>
        body { font-family: Arial, sans-serif; margin: 32px; color: #111; }
        h1 { font-size: 20px; text-align: center; margin: 0; }
        .options { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 20px; font-size: 12px; margin-left: 20px; }
        @media print { body { margin: 16px; } }
      </style></head><body>
      ${ref.current.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.onload = () => {
      const css = win.document.getElementById("ml-css");
      if (css && !css.sheet) { css.onload = () => win.print(); }
      else { win.print(); }
    };
  };

  const totalMarks = paper.sections?.reduce((s, sec) =>
    s + (sec.questions?.reduce((sq, q) => sq + parseFloat(q.marks || sec.marks_per_q || 0), 0) || 0), 0) || 0;

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, width: "90vw", maxWidth: 780,
        maxHeight: "92vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid #e5e7eb",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#1e1b4b",
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>Print Preview</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handlePrint} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 16px",
              borderRadius: 8, border: "none", background: "#4f46e5", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>
              <Printer size={14} /> Print / Save PDF
            </button>
            <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#a5b4fc" }}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px", background: "#f8fafc" }}>
          <div ref={ref} style={{
            background: "#fff", padding: "32px 36px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.08)", borderRadius: 8, maxWidth: 680, margin: "0 auto",
          }}>
            {paper.header_text && (
              <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 6 }}>{paper.header_text}</div>
            )}
            <h1 style={{ fontSize: 20, textAlign: "center", margin: "0 0 6px", fontWeight: 800 }}>{paper.title}</h1>
            <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 4 }}>
              {[paper.class_name, paper.subject_name].filter(Boolean).join(" · ")}
              {paper.paper_set ? ` | Set: ${paper.paper_set}` : ""}
              {paper.year ? ` | Year: ${paper.year}` : ""}
            </div>
            <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 10 }}>
              {paper.duration_minutes ? `Time: ${paper.duration_minutes} min` : ""}
              {paper.duration_minutes && totalMarks ? " | " : ""}
              {totalMarks ? `Total Marks: ${totalMarks}` : ""}
            </div>
            <div style={{ borderTop: "2px solid #111", borderBottom: "2px solid #111", margin: "10px 0", padding: "1px 0" }} />
            {paper.instructions && (
              <div style={{ fontSize: 12, background: "#fffbeb", padding: "8px 12px", borderLeft: "3px solid #fbbf24", margin: "12px 0", lineHeight: 1.7 }}>
                <strong>Instructions:</strong> {paper.instructions}
              </div>
            )}

            {paper.sections?.map((sec) => (
              <div key={sec.id}>
                <div style={{ fontWeight: 800, fontSize: 14, marginTop: 20, marginBottom: 4 }}>
                  SECTION {sec.section_label}{sec.title ? ` — ${sec.title}` : ""}
                </div>
                {sec.instructions && <div style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>{sec.instructions}</div>}
                <div style={{ borderBottom: "1px solid #ddd", marginBottom: 10 }} />

                {sec.questions?.map((q, qi) => {
                  const renderFillBlank = (text) => text.split(/(_+)/g).map((part, i) =>
                    /^_+$/.test(part)
                      ? <span key={i} style={{ display: "inline-block", minWidth: 80, borderBottom: "1.5px solid #111", marginLeft: 2, marginRight: 2 }}>&nbsp;</span>
                      : part
                  );
                  const renderMatch = (text) => {
                    const { instruction, colA = [], colB = [], answers = [] } = parseMatchData(text);
                    const letters = colB.map((_, i) => String.fromCharCode(97 + i));
                    return (
                      <>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, fontSize: 12 }}>
                          <table style={{ borderCollapse: "collapse", width: "100%" }}>
                            <thead><tr>
                              <th style={{ padding: "4px 8px", border: "1px solid #ccc", background: "#f3f4f6", fontSize: 11 }}>No.</th>
                              <th style={{ padding: "4px 8px", border: "1px solid #ccc", background: "#f3f4f6", fontSize: 11 }}>Column A</th>
                              <th style={{ padding: "4px 8px", border: "1px solid #ccc", background: "#f3f4f6", fontSize: 11 }}>Ans.</th>
                            </tr></thead>
                            <tbody>
                              {colA.map((a, i) => (
                                <tr key={i}>
                                  <td style={{ padding: "4px 6px", border: "1px solid #ddd", textAlign: "center" }}>{i + 1}.</td>
                                  <td style={{ padding: "4px 8px", border: "1px solid #ddd" }}>{a}</td>
                                  <td style={{ padding: "4px 6px", border: "1px solid #ddd", textAlign: "center" }}>
                                    <span style={{ display: "inline-block", minWidth: 24, borderBottom: "1px solid #111" }}>&nbsp;</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <table style={{ borderCollapse: "collapse", width: "100%" }}>
                            <thead><tr>
                              <th style={{ padding: "4px 8px", border: "1px solid #ccc", background: "#f3f4f6", fontSize: 11 }}>Opt.</th>
                              <th style={{ padding: "4px 8px", border: "1px solid #ccc", background: "#f3f4f6", fontSize: 11 }}>Column B</th>
                            </tr></thead>
                            <tbody>
                              {colB.map((b, i) => (
                                <tr key={i}>
                                  <td style={{ padding: "4px 6px", border: "1px solid #ddd", textAlign: "center" }}>{letters[i]}.</td>
                                  <td style={{ padding: "4px 8px", border: "1px solid #ddd" }}>{b}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  };

                  return (
                    <div key={q.id} style={{ marginBottom: 18 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.7 }}>
                        Q{q.question_no || qi + 1}.{" "}
                        {sec.question_type === "match_following" ? (() => { try { const p = JSON.parse(q.question_text || "{}"); return p.instruction || "Match the Following"; } catch { return q.question_text; } })() : sec.question_type === "fill_blank" ? renderFillBlank(q.question_text) : <MathPreview latex={q.question_text} />}
                        {(q.marks || sec.marks_per_q) && (
                          <span style={{ float: "right", fontSize: 12, fontWeight: 400, color: "#555" }}>[{q.marks || sec.marks_per_q} mark{parseFloat(q.marks || sec.marks_per_q) !== 1 ? "s" : ""}]</span>
                        )}
                      </div>
                      {q.options?.length > 0 && sec.question_type !== "match_following" && (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px", fontSize: 12, marginTop: 6, marginLeft: 20 }}>
                          {q.options.map((opt) => (
                            <div key={opt.id} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              <span>({opt.option_label})</span>
                              <MathPreview latex={opt.option_text || "___"} />
                            </div>
                          ))}
                        </div>
                      )}
                      {sec.question_type === "match_following" && (
                        <div style={{ marginLeft: 20, marginTop: 4 }}>{renderMatch(q.question_text)}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN — Manual Paper Builder
══════════════════════════════════════ */
export default function ManualPaperBuilder() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const isTeacher = window.location.pathname.startsWith("/teacher-lms");
  const examsPath = isTeacher ? "/teacher-lms/exams" : "/lms/exams";

  const [paper,        setPaper]        = useState(null);
  const [examData,     setExamData]     = useState(null);
  const [maxQuestions, setMaxQuestions] = useState(0);
  const [chapters,     setChapters]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [addSection,   setAddSection]   = useState(false);
  const [showPreview,  setShowPreview]  = useState(false);
  const [confirmSec,   setConfirmSec]   = useState(null);
  const [savingPub,    setSavingPub]    = useState(false);

  const loadPaper = async () => {
    try {
      const res = await lmsGetManualPaperById(id);
      const p = res.data;
      setPaper(p);

      // Fetch chapters for chapter assignment
      if (p?.school_id) {
        lmsGetChapters({ school_id: p.school_id })
          .then((cr) => setChapters(cr?.data?.data || []))
          .catch(() => {});
      }

      // Fetch exam to get num_questions limit + date/time
      if (p?.exam_id) {
        try {
          const userData = JSON.parse(localStorage.getItem("userData") || "{}");
          const schoolId = userData.school_id || userData.admin_id || null;
          const eRes = await lmsGetExams({ school_id: schoolId });
          const exams = eRes?.data?.data || [];
          const exam = exams.find((e) => String(e.id) === String(p.exam_id));
          if (exam) {
            setExamData(exam);
            if (exam.num_questions) setMaxQuestions(parseInt(exam.num_questions) || 0);
          }
        } catch {}
      }
    } catch { toast.error("Failed to load paper"); }
    finally { setLoading(false); }
  };


  useEffect(() => { loadPaper(); }, [id]);

  const totalQ     = paper?.sections?.reduce((s, sec) => s + (sec.questions?.length || 0), 0) || 0;
  const totalMarks = paper?.sections?.reduce((s, sec) =>
    s + (sec.questions?.reduce((sq, q) => sq + parseFloat(q.marks || sec.marks_per_q || 0), 0) || 0), 0) || 0;

  // Live notification when marks or questions go out of range
  useEffect(() => {
    if (loading || !paper || !examData) return;
    const examMarks = parseInt(examData.total_marks) || 0;
    const examQ     = parseInt(examData.num_questions) || 0;
    if (examMarks > 0 && Math.round(totalMarks) > examMarks) {
      toast.error(`Marks exceeded! Paper has ${Math.round(totalMarks)}/${examMarks} marks. Adjust section Marks/Q.`, { toastId: "marks-over" });
    }
    if (examQ > 0 && totalQ > examQ) {
      toast.error(`Questions exceeded! Paper has ${totalQ}/${examQ} questions. Remove extra questions.`, { toastId: "q-over" });
    }
  }, [totalMarks, totalQ]);

  const handleAddSection = async (form) => {
    try {
      await lmsCreateSection(id, {
        section_label: form.section_label, title: form.title || null,
        question_type: form.question_type,
        marks_per_q: parseFloat(form.marks_per_q) || 1,
        instructions: form.instructions || null,
        sort_order: paper?.sections?.length || 0,
      });
      toast.success("Section added");
      loadPaper();
    } catch (err) { toast.error(err?.response?.data?.message || "Failed to add section"); }
  };

  const handleDeleteSection = async () => {
    try {
      await lmsDeleteSection(confirmSec.id);
      toast.success("Section deleted");
      setConfirmSec(null);
      loadPaper();
    } catch { toast.error("Failed to delete section"); }
  };

  const togglePublish = async () => {
    // Compute live from current paper state at call time
    const currentTotalQ = paper?.sections?.reduce((s, sec) => s + (sec.questions?.length || 0), 0) || 0;
    const currentTotalMarks = paper?.sections?.reduce((s, sec) =>
      s + (sec.questions?.reduce((sq, q) => sq + parseFloat(q.marks || sec.marks_per_q || 0), 0) || 0), 0) || 0;

    // Validate only when publishing
    if (!paper.is_published) {
      if (currentTotalQ === 0) {
        toast.error("Please add at least one question before publishing.");
        return;
      }
      if (maxQuestions > 0 && currentTotalQ < maxQuestions) {
        toast.error(`Paper has ${currentTotalQ}/${maxQuestions} questions. Add ${maxQuestions - currentTotalQ} more question(s) to publish.`);
        return;
      }
      if (maxQuestions > 0 && currentTotalQ > maxQuestions) {
        toast.error(`Paper has ${currentTotalQ}/${maxQuestions} questions. Remove ${currentTotalQ - maxQuestions} question(s) to publish.`);
        return;
      }
      if (examData?.total_marks && Math.round(currentTotalMarks) !== parseInt(examData.total_marks)) {
        toast.error(`Paper marks (${Math.round(currentTotalMarks)}) must equal exam total marks (${examData.total_marks}). Adjust section Marks/Q before publishing.`);
        return;
      }
    }
    setSavingPub(true);
    try {
      const newVal = paper.is_published ? 0 : 1;
      await lmsUpdateManualPaper(id, { is_published: newVal });

      toast.success(newVal ? "Paper published successfully!" : "Paper set to draft");
      loadPaper();
    } catch { toast.error("Failed to update"); }
    finally { setSavingPub(false); }
  };

  const nextSectionLabel = SECTION_LABELS[paper?.sections?.length || 0] || "A";

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
      <Loader size={28} className="ms2-spin" color="#4f46e5" />
    </div>
  );

  if (!paper) return (
    <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Paper not found.</div>
  );

  return (
    <div className="lms-content-inner">
      {/* Header */}
      <div style={{
        background: "#fff", borderRadius: 16, marginBottom: 24,
        border: "1px solid #e5e7eb", boxShadow: "0 4px 16px rgba(79,70,229,0.08)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#7c3aed,#0891b2)" }} />
        {/* Single row */}
        <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: 6 }}>

          {/* Back */}
          <button onClick={() => navigate(examsPath)} style={{
            display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600,
            color: "#374151", background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10,
            padding: "7px 14px", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
          }}>
            <ArrowLeft size={14} /> Back
          </button>

          <div style={{ width: 1, height: 36, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />

          {/* Name / Class / Subject */}
          {[
            { icon: <FileText size={16} color="#ec4899" />, iconBg: "#fdf2f8", label: "Exam Name", value: paper.title, maxW: 160 },
            paper.class_name   && { icon: <BookOpen size={16} color="#3b82f6" />, iconBg: "#eff6ff", label: "Class",   value: paper.class_name,   maxW: 100 },
            paper.subject_name && { icon: <Layers   size={16} color="#10b981" />, iconBg: "#ecfdf5", label: "Subject", value: paper.subject_name, maxW: 120 },
          ].filter(Boolean).map((item, i, arr) => (
            <React.Fragment key={i}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: item.maxW === 160 ? 1 : 0 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: item.iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1.2 }}>{item.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: item.maxW }}>
                    {item.value}
                  </div>
                </div>
              </div>
              {i < arr.length - 1 && <div style={{ width: 1, height: 36, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />}
            </React.Fragment>
          ))}

          <div style={{ width: 1, height: 36, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />

          {/* Sections */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Layers size={16} color="#7c3aed" />
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1.2 }}>Section</div>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#7c3aed" }}>{paper.sections?.length || 0}</span>
            </div>
          </div>

          <div style={{ width: 1, height: 36, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />

          {/* Questions */}
          {(() => {
            const qColor = maxQuestions > 0 && totalQ !== maxQuestions ? "#d97706" : "#0891b2";
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "#fff7ed", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <BookOpen size={16} color={qColor} />
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1.2 }}>Questions</div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: qColor }}>
                    {maxQuestions > 0 ? <>{totalQ} <span style={{ color: "#9ca3af", fontWeight: 500 }}>/ {maxQuestions}</span></> : totalQ}
                  </span>
                </div>
              </div>
            );
          })()}

          <div style={{ width: 1, height: 36, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />

          {/* Marks */}
          {(() => {
            const mColor = examData?.total_marks && Math.round(totalMarks) !== parseInt(examData.total_marks) ? "#ef4444" : "#16a34a";
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <BarChart2 size={16} color={mColor} />
                </div>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", lineHeight: 1.2 }}>Marks</div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: mColor }}>
                    {examData?.total_marks ? <>{Math.round(totalMarks)} <span style={{ color: "#9ca3af", fontWeight: 500 }}>/ {examData.total_marks}</span></> : Math.round(totalMarks)}
                  </span>
                </div>
              </div>
            );
          })()}

          <div style={{ width: 1, height: 36, background: "#e5e7eb", flexShrink: 0, margin: "0 4px" }} />

          {/* Status pill */}
          <span style={{
            fontSize: 12, fontWeight: 700, padding: "5px 12px", borderRadius: 20, flexShrink: 0,
            background: paper.is_published ? "#dcfce7" : "#fef9c3",
            color: paper.is_published ? "#16a34a" : "#b45309",
            border: `1.5px solid ${paper.is_published ? "#bbf7d0" : "#fde68a"}`,
          }}>
            {paper.is_published ? "Published" : "Draft"}
          </span>

          <div style={{ flex: 1 }} />

          {/* Actions */}
          <button onClick={() => setShowPreview(true)} style={{
            display: "flex", alignItems: "center", gap: 5, padding: "7px 14px",
            borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff",
            color: "#374151", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0,
          }}>
            <Eye size={14} /> Preview
          </button>
          <button onClick={togglePublish} disabled={savingPub} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "7px 16px",
            borderRadius: 10, border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0,
            background: paper.is_published ? "linear-gradient(135deg,#16a34a,#15803d)" : "linear-gradient(135deg,#4f46e5,#7c3aed)",
            color: "#fff", boxShadow: "0 2px 8px rgba(79,70,229,0.25)",
          }}>
            {savingPub ? <Loader size={13} className="ms2-spin" /> : (paper.is_published ? <Check size={13} /> : <BookOpen size={13} />)}
            {paper.is_published ? "Published" : "Publish Paper"}
          </button>
        </div>
      </div>


      {paper.sections?.length === 0 && (
        <div style={{
          textAlign: "center", padding: "60px 20px",
          background: "#fff", borderRadius: 16, border: "2px dashed #e5e7eb",
          marginBottom: 16,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 20, background: "linear-gradient(135deg,#ede9fe,#dbeafe)",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px",
          }}>
            <BookOpen size={32} color="#4f46e5" />
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", marginBottom: 6 }}>No sections yet</div>
          <div style={{ fontSize: 14, color: "#9ca3af", maxWidth: 320, margin: "0 auto" }}>
            Start building your question paper by adding sections below. Each section can have a different question type.
          </div>
        </div>
      )}

      {paper.sections?.map((sec, si) => (
        <SectionCard
          key={sec.id}
          section={sec}
          index={si}
          chapters={chapters}
          onRefresh={loadPaper}
          onDelete={() => setConfirmSec(sec)}
          maxQuestions={maxQuestions}
          currentTotalQ={totalQ}
          examTotalMarks={parseInt(examData?.total_marks) || 0}
          currentTotalMarks={totalMarks}
        />
      ))}

      {(() => {
        const marksReached    = examData?.total_marks && totalMarks >= parseInt(examData.total_marks);
        const questionsReached = maxQuestions > 0 && totalQ >= maxQuestions;
        const isDisabled      = marksReached || questionsReached;
        const pendingMarks    = examData?.total_marks ? parseInt(examData.total_marks) - Math.round(totalMarks) : null;
        const pendingQ        = maxQuestions > 0 ? maxQuestions - totalQ : null;

        return (
          <>
            <button
              onClick={() => {
                if (marksReached) { toast.error(`Marks limit reached (${Math.round(totalMarks)}/${examData.total_marks}). Cannot add more sections.`); return; }
                if (questionsReached) { toast.error(`Question limit reached (${totalQ}/${maxQuestions}). Cannot add more sections.`); return; }
                setAddSection(true);
              }}
              style={{
                width: "100%", padding: "16px", borderRadius: 14,
                border: `2px dashed ${isDisabled ? "#e5e7eb" : "#a5b4fc"}`,
                background: isDisabled ? "#f9fafb" : "linear-gradient(135deg,#f5f3ff,#ede9fe)",
                color: isDisabled ? "#9ca3af" : "#4f46e5",
                fontSize: 15, fontWeight: 800,
                cursor: isDisabled ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                marginTop: 8, transition: "all 0.15s",
                opacity: isDisabled ? 0.7 : 1,
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: isDisabled ? "#d1d5db" : "#4f46e5",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Plus size={16} color="#fff" />
              </div>
              {isDisabled ? (
                <span>
                  Add New Section
                  <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 8, color: "#ef4444" }}>
                    {marksReached && pendingMarks !== null && pendingMarks <= 0
                      ? `(Marks full: ${Math.round(totalMarks)}/${examData.total_marks})`
                      : questionsReached
                      ? `(Questions full: ${totalQ}/${maxQuestions})`
                      : ""}
                  </span>
                </span>
              ) : (
                <span>
                  Add New Section
                  {(pendingMarks !== null || pendingQ !== null) && (
                    <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 8, color: "#6366f1" }}>
                      {[
                        pendingMarks !== null && pendingMarks > 0 && `${pendingMarks} marks pending`,
                        pendingQ !== null && pendingQ > 0 && `${pendingQ} questions pending`,
                      ].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </span>
              )}
            </button>
          </>
        );
      })()}

      {addSection && (
        <AddSectionModal
          nextLabel={nextSectionLabel}
          onClose={() => setAddSection(false)}
          onSave={handleAddSection}
          defaultMarksPerQ="1"
          pendingMarks={examData?.total_marks ? parseInt(examData.total_marks) - Math.round(totalMarks) : null}
          pendingQ={maxQuestions > 0 ? maxQuestions - totalQ : null}
        />
      )}
      {confirmSec && (
        <ConfirmModal
          message={<>Delete <strong>Section {confirmSec.section_label}</strong> and all its questions?</>}
          onConfirm={handleDeleteSection}
          onCancel={() => setConfirmSec(null)}
        />
      )}
      {showPreview && (
        <PrintPreview paper={paper} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
}
